import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject } from 'rxjs';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { UtilitiesService } from '../services/utilities.service';
import { DataEnvService } from '../shared/sl-data/data-env.service';
import { UserDialogComponent } from './user-dialog/user-dialog.component';
import { AuthService } from '../core/auth.service';

export interface UserData {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  password: string;
  id?: string;
  isActive: boolean;
}

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = ['email', 'firstName', 'lastName', 'roles', 'status', 'actions'];
  dataSource = new MatTableDataSource<UserData>();
  loading$ = new BehaviorSubject<boolean>(false);
  availableRoles: string[] = ['admin', 'user', 'manager'];

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private fns: AngularFireFunctions,
    private dialog: MatDialog,
    private utilities: UtilitiesService,
    private dataEnv: DataEnvService,
    private snackBar: MatSnackBar,
    private auth: AuthService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.dataEnv.setComFromCurrentUser();
    await this.loadUsers();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  async loadUsers(): Promise<void> {
    this.loading$.next(true);
    try {
      const callable = this.fns.httpsCallable('getUsers');
      const result = await callable({}).toPromise();
      this.dataSource.data = result;
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    } catch (error) {
      console.error('Error loading users:', error);
      this.utilities.notifyError('Failed to load users');
    } finally {
      this.loading$.next(false);
    }
  }

  async updateUser(user: UserData) {
    try {
      const callable = this.fns.httpsCallable('updateUser');
      await callable(user).toPromise();
      await this.loadUsers();
      this.utilities.notifySuccess('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      this.utilities.notifyError(`Failed to create user: ${error.details || 'Unknown Error'}`);
    }
  }

  async toggleUserStatus(user: UserData) {
      user.isActive = !user.isActive;
      await this.updateUser(user);
  }

  async createUser(user: UserData) {
    try {
      const callable = this.fns.httpsCallable('createUser');
      await callable(user).toPromise();
      await this.loadUsers();
      this.utilities.notifySuccess('User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      this.utilities.notifyError(`Failed to create user: ${error.details || 'Unknown Error'}`);
    }
  }

  openCreateUserDialog(): void {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      width: '400px',
      data: { isEdit: false }
    });

    dialogRef.afterClosed().subscribe(async (result: UserData) => {
      if (result) {
        // Add new user to dataSource
        await this.createUser(result);
      }
    });
  }

  openEditUserDialog(user: UserData): void {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      width: '400px',
      data: { 
        isEdit: true,
        user: { ...user }
      }
    });

    dialogRef.afterClosed().subscribe(async (user: UserData) => {
        if (user) {
          // Add new user to dataSource
          await this.updateUser(user);
        }
      });
  }

  async deleteUser(user: UserData): Promise<void> {
    // Show confirmation dialog
    const confirmDelete = confirm(`Are you sure you want to delete user ${user.email}?`);
    if (!confirmDelete) return;

    try {
      this.loading$.next(true);
      const callable = this.fns.httpsCallable('deleteUser');
      await callable({ userId: user.id }).toPromise();
      
      this.utilities.notifySuccess('User deleted successfully');
      await this.loadUsers(); // Reload the users list
    } catch (error) {
      console.error('Error deleting user:', error);
      this.utilities.notifyError(`Failed to delete user: ${error.details || 'Unknown Error'}`);
    } finally {
      this.loading$.next(false);
    }
  }

  public currentUser$ = this.auth.user$;
  public hasRole$ = this.dataEnv.hasRole$.bind(this.dataEnv);

}
